import { useFetch, useFetchPageListData } from '@/composables'
import type { SymbolListRow } from '@/types'
import { useTradeStore, useSocketStore } from '@/store'
import { watch } from 'vue'
import type {
  Datafeed,
  SymbolInfo,
  Period,
  DatafeedSubscribeCallback,
  KLineData,
  MarketData,
} from './types'

// 映射周期字符串到分钟数
const timeSpanToMinutes: Record<string, number> = {
  minute: 1,
  hour: 60,
  day: 1440,
  week: 10080,
  month: 43200,
  year: 525600,
}

const { fetchData: _fetchSymbols } = useFetchPageListData<SymbolListRow>({
  url: '/api/trade/symbol',
  autoFetch: false,
})

const { fetchData } = useFetch<MarketData[]>({
  url: '/api/kchart/chart',
  autoFetch: false,
})

export const ONE_MINUTE_MS = 60 * 1000
export const getPeriodStart = (timestamp: number, minutes: number) => {
  // 对齐周K：将 epoch 往前偏移 3 天，使得一周从周一 00:00 开始
  if (minutes === 10080) {
    const weekOffsetMs = 4 * 24 * 60 * ONE_MINUTE_MS
    return (
      Math.floor((timestamp + weekOffsetMs) / (minutes * ONE_MINUTE_MS)) * minutes * ONE_MINUTE_MS -
      weekOffsetMs
    )
  }

  return Math.floor(timestamp / (minutes * ONE_MINUTE_MS)) * minutes * ONE_MINUTE_MS
}

const getUtcTimestamp = (
  timestamp: number,
  periodInMinutes: number,
  serverOffsetSeconds: number,
) => {
  return periodInMinutes >= 1440 ? timestamp * 1000 : (timestamp - serverOffsetSeconds) * 1000
}

export class CustomDatafeed implements Datafeed {
  private exchangeName: string
  private priceCurrency: string
  private loginId: string
  private market: string
  private serverOffsetSeconds: number
  private _apiKey: string
  private currentSymbol: SymbolInfo | null = null
  private currentPeriodInMinutes: number | null = null
  private callback: DatafeedSubscribeCallback | null = null
  private currentBar: KLineData | null = null
  private unwatchQuote: (() => void) | null = null
  private currentPeriod: Period | null = null
  // 切换品种的市场时，需要重新订阅
  private _prevSymbolMarket?: string
  /** 缓存 symbol+resolution 是否已到最早历史 */
  private historyExhaustedMap = new Map<string, boolean>()
  private symbolFirstRequestMap: Map<number, boolean> = new Map()
  private _ws?: WebSocket
  /** 最后一根K线 */
  private latestBar: KLineData | undefined = undefined

  /** Pinia trade store – 初始化时再获取，避免在模块顶层调用 useTradeStore() */
  private tradeStore = useTradeStore()
  private socketStore = useSocketStore()
  constructor({
    loginId,
    exchangeName,
    market,
    priceCurrency,
    serverOffsetSeconds,
    apiKey,
  }: {
    loginId: string
    exchangeName: string
    market: string
    priceCurrency: string
    serverOffsetSeconds: number
    apiKey: string
  }) {
    this.loginId = loginId
    this.priceCurrency = priceCurrency
    this.exchangeName = exchangeName
    this.market = market
    this.serverOffsetSeconds = serverOffsetSeconds
    this._apiKey = apiKey
  }

  // 切换到新 symbol 时，把与旧 symbol 相关的状态清掉，避免复用旧 latestBar / 缓存
  private resetForNewSymbol(next: SymbolInfo) {
    this.currentSymbol = next
    this.latestBar = undefined
    this.currentPeriodInMinutes = null
    this.symbolFirstRequestMap.clear()
    this.historyExhaustedMap.clear()
  }

  async searchSymbols(search?: string): Promise<SymbolInfo[]> {
    try {
      const data = await _fetchSymbols({
        exchange_name: this.exchangeName,
        loginid: this.loginId,
        keyword: search,
        type: '',
        page: 1,
        pageSize: 9999,
      })
      return (data?.list ?? []).map(({ symbol, description, type }) => ({
        ticker: symbol,
        name: description,
        shortName: symbol,
        market: this.market,
        exchange: this.exchangeName,
        priceCurrency: this.priceCurrency,
        type: type,
        logo: undefined,
      }))
    } catch (error) {
      return [] as SymbolInfo[]
    }
  }

  async getHistoryKLineData(
    symbol: SymbolInfo,
    period: Period,
    from: number,
    to: number,
  ): Promise<KLineData[]> {
    // 复杂逻辑：若传入的 symbol 与当前不同，先做一次“切品种”清理
    if (!this.currentSymbol || this.currentSymbol.ticker !== symbol.ticker) {
      this.resetForNewSymbol(symbol)
    }
    const periodInMinutes = this.getIntervalMinutes(period)
    const cacheKey = `${symbol.ticker}_${periodInMinutes}`
    const isChangePeriod = periodInMinutes !== this.currentPeriodInMinutes
    if (isChangePeriod) {
      this.symbolFirstRequestMap.clear()
      this.historyExhaustedMap.clear()
    }
    const reachedEnd = this.historyExhaustedMap.get(cacheKey) ?? false
    const isFirstFetch = this.symbolFirstRequestMap.get(periodInMinutes) ?? true
    const shouldFetch = isFirstFetch || !reachedEnd
    if (!shouldFetch) {
      return []
    }
    const barCount = this.getBarCount(from, to, periodInMinutes)
    const barsToFetch = Math.max(barCount, 50)

    try {
      const params: Record<string, any> = {
        symbol: symbol.ticker,
        exchange_name: symbol.exchange,
        loginid: this.loginId,
        date: periodInMinutes,
        count: barsToFetch,
      }
      if (!isFirstFetch) {
        params.end = Math.floor(to / 1000)
      }
      const data = await fetchData(params)
      const kineData = (data || []).map(({ h, l, o, c, t, v }) => ({
        timestamp: getUtcTimestamp(t, periodInMinutes, this.serverOffsetSeconds),
        open: o,
        high: h,
        low: l,
        close: c,
        volume: v,
      }))
      this.currentPeriodInMinutes = periodInMinutes
      this.symbolFirstRequestMap.set(periodInMinutes, false)
      this.historyExhaustedMap.set(cacheKey, kineData.length < barsToFetch)
      const lastKine = kineData[kineData.length - 1]
      if (!this.latestBar || lastKine.timestamp >= this.latestBar.timestamp) {
        this.latestBar = kineData[kineData.length - 1]
      }
      return kineData
    } catch (error) {
      console.error('获取历史K线数据失败:', error)
      return []
    }
  }

  subscribe(symbolInfo: SymbolInfo, period: Period, callback: DatafeedSubscribeCallback): void {
    // 清理之前的订阅
    this.unsubscribe(symbolInfo, period)
    // 复杂逻辑：订阅时也确保状态与 symbol 对齐（首次订阅或库层未提前调用 getHistory 的场景）
    if (!this.currentSymbol || this.currentSymbol.ticker !== symbolInfo.ticker) {
      this.resetForNewSymbol(symbolInfo)
    }
    this.currentPeriod = period
    this.callback = callback

    const periodInMinutes = this.getIntervalMinutes(period)
    this.unwatchQuote = watch(
      () => this.socketStore.quoteTick,
      async () => {
        const quote = this.socketStore.lastQuoteMap.get(symbolInfo.ticker)
        if (!quote) return
        const { bid: price, ts: tickTimestamp } = quote
        const tickUtcTimestamp = getUtcTimestamp(
          tickTimestamp,
          periodInMinutes,
          this.serverOffsetSeconds,
        )

        const barPeriodStart = getPeriodStart(tickUtcTimestamp, periodInMinutes)
        const tickBar: KLineData = {
          timestamp: tickUtcTimestamp,
          open: price,
          high: price,
          low: price,
          close: price,
          volume: 1,
        }
        if (this.latestBar) {
          const lastBarStart = getPeriodStart(this.latestBar.timestamp, periodInMinutes)
          if (barPeriodStart === lastBarStart) {
            // 在同一根K线内
            this.latestBar.high = Math.max(this.latestBar.high, price)
            this.latestBar.low = Math.min(this.latestBar.low, price)
            this.latestBar.close = price
            this.latestBar.volume = (this.latestBar.volume || 0) + 1
            callback({ ...this.latestBar })
          } else if (barPeriodStart > lastBarStart) {
            // 🆕 新周期，检查是否有缺口
            const gapBars = Math.floor(
              (barPeriodStart - lastBarStart) / (periodInMinutes * ONE_MINUTE_MS),
            )
            // if (gapBars > 1) {
            //   /*
            //    * 缺口补齐 —— 方案 B：请求真实历史
            //    * 我们希望拿到 gapBars-1 根，结束时间为当前 bar 起点，
            //    * TradingView 要求时间从旧→新排序，因此拿到后直接 push 即可。
            //    */
            //   try {
            //     const resp = await fetchData({
            //       symbol: symbolInfo.name,
            //       date: periodInMinutes,
            //       exchange_name: symbolInfo.exchange,
            //       loginid: this.loginId,
            //     })

            //     const fixBars = (resp || []).map(({ c, o, h, l, v, t }: any) => {
            //       return {
            //         timestamp: getUtcTimestamp(t, periodInMinutes, this.serverOffsetSeconds),
            //         close: c,
            //         open: o,
            //         high: h,
            //         low: l,
            //         volume: v,
            //       } as KLineData
            //     })

            //     fixBars.forEach((b: KLineData) => {
            //       // realtimeAccumulatedBars.push(b)
            //       callback(b)
            //     })
            //   } catch (err) {
            //     console.error('补历史 getKchart 失败', err)
            //   }
            // }
            this.latestBar = tickBar
            callback(tickBar)
          } else {
            // 比上一根K线时间戳小，说明是旧数据
          }
        } else {
          this.latestBar = tickBar
          callback(tickBar)
        }
      },
      { deep: true },
    )
  }

  unsubscribe(symbol: SymbolInfo, period: Period): void {
    // 发送最后的K线
    // if (this.currentBar && this.callback) {
    //   this.callback(this.currentBar)
    //   this.currentBar = null
    // }
    // 移除监听
    if (this.unwatchQuote) {
      this.unwatchQuote()
      this.unwatchQuote = null
    }
    this.callback = null
    this.currentBar = null
    this.latestBar = undefined
  }

  private getIntervalMinutes(period: Period): number {
    return period.multiplier * timeSpanToMinutes[period.timespan]
  }

  private getBarCount(from: number, to: number, periodInMinutes: number): number {
    const barIntervalMs = periodInMinutes * 60 * 1000
    return Math.ceil((to - from) / barIntervalMs)
  }
}
